import { useState, useMemo } from 'react';
import { cn } from '../../lib/cn';

export default function GrammarObservationTags({
  observations = [],
  onRemove,
  onAdd,
  conceptsList = [],
  readOnly = false,
}) {
  const [showAddForm, setShowAddForm] = useState(false);

  const conceptLabelMap = useMemo(() => {
    const map = {};
    for (const c of conceptsList) {
      map[c.id] = c.label;
    }
    return map;
  }, [conceptsList]);

  if (!observations.length && readOnly) return null;

  return (
    <div className="space-y-2">
      {observations.length > 0 && (
        <div className="flex justify-end">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {observations.length} {observations.length === 1 ? 'observation' : 'observations'}
          </span>
        </div>
      )}

      {observations.length === 0 && !readOnly && (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic">
          No grammar observations yet. Save with transcription verified to trigger AI evaluation.
        </p>
      )}

      <div className="space-y-1.5">
        {observations.map(obs => (
          <ObservationCard
            key={obs.id}
            observation={obs}
            conceptLabel={conceptLabelMap[obs.concept_id] || obs.concept_id}
            onRemove={onRemove}
            readOnly={readOnly}
          />
        ))}
      </div>

      {!readOnly && onAdd && (
        <div>
          {!showAddForm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="px-2 py-0.5 rounded-full border border-dashed border-gray-300 dark:border-gray-600 text-xs text-gray-500 dark:text-gray-400 hover:border-gray-400 hover:text-gray-600 dark:hover:border-gray-500 dark:hover:text-gray-300 transition-colors"
            >
              + Add
            </button>
          )}
          {showAddForm && (
            <AddObservationForm
              conceptsList={conceptsList}
              onSubmit={(data) => {
                onAdd(data);
                setShowAddForm(false);
              }}
              onCancel={() => setShowAddForm(false)}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ObservationCard({ observation, conceptLabel, onRemove, readOnly }) {
  const isError = observation.observation_type === 'error';

  return (
    <div className={cn(
      'p-2.5 rounded-lg border text-xs space-y-1',
      isError
        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
        : 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
    )}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 space-y-1">
          {/* Excerpt and correction */}
          <div className="text-gray-800 dark:text-gray-200">
            <span className="italic text-gray-600 dark:text-gray-400">&ldquo;{observation.excerpt}&rdquo;</span>
            {observation.correction && (
              <>
                <span className="mx-1.5 text-gray-400 dark:text-gray-500">&rarr;</span>
                <span className="font-medium">{observation.correction}</span>
              </>
            )}
          </div>

          {/* Explanation */}
          {observation.explanation && (
            <p className="text-gray-600 dark:text-gray-400">
              {observation.explanation}
            </p>
          )}

          {/* Concept label + badges */}
          <div className="flex items-center gap-1.5 pt-0.5">
            <span className={cn(
              'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium',
              isError
                ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            )}>
              {conceptLabel}
            </span>
            {observation.ai_detected && (
              <span className="inline-block px-1.5 py-0.5 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 rounded text-[10px]">
                AI
              </span>
            )}
          </div>
        </div>

        {/* Remove button */}
        {!readOnly && onRemove && (
          <button
            onClick={() => onRemove(observation.id)}
            className="shrink-0 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            title="Remove observation"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

function AddObservationForm({ conceptsList, onSubmit, onCancel }) {
  const [conceptId, setConceptId] = useState('');
  const [observationType, setObservationType] = useState('error');
  const [excerpt, setExcerpt] = useState('');
  const [correction, setCorrection] = useState('');
  const [explanation, setExplanation] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!conceptId || !excerpt) return;
    onSubmit({ concept_id: conceptId, observation_type: observationType, excerpt, correction, explanation });
  };

  return (
    <form onSubmit={handleSubmit} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 space-y-2">
      <div className="flex gap-2">
        <select
          value={conceptId}
          onChange={(e) => setConceptId(e.target.value)}
          className="flex-1 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs"
          required
        >
          <option value="">Select concept...</option>
          {conceptsList.map(c => (
            <option key={c.id} value={c.id}>{c.label}</option>
          ))}
        </select>
        <select
          value={observationType}
          onChange={(e) => setObservationType(e.target.value)}
          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs"
        >
          <option value="error">Error</option>
          <option value="correct_usage">Correct usage</option>
        </select>
      </div>
      <input
        type="text"
        value={excerpt}
        onChange={(e) => setExcerpt(e.target.value)}
        placeholder="Excerpt from response..."
        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs"
        required
      />
      <input
        type="text"
        value={correction}
        onChange={(e) => setCorrection(e.target.value)}
        placeholder="Correction (optional)"
        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs"
      />
      <input
        type="text"
        value={explanation}
        onChange={(e) => setExplanation(e.target.value)}
        placeholder="Explanation (optional)"
        className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 text-xs"
      />
      <div className="flex gap-2">
        <button type="submit" className="px-3 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700">
          Add
        </button>
        <button type="button" onClick={onCancel} className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 text-xs hover:bg-gray-100 dark:hover:bg-gray-700">
          Cancel
        </button>
      </div>
    </form>
  );
}
